import React, { useMemo } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import AdbIcon from '@mui/icons-material/Adb';
import Link from '@mui/material/Link';
import ThemeToggleButton from './theme/ThemeToggleButtons.js';
import { useTheme } from '@mui/material/styles';
import { useUserRole } from '../auth/useUserRole.ts';

const NavBar = () => {
  const theme = useTheme();
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const role = useUserRole();

  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const userMenuItems = useMemo(() => {
    if (isSignedIn) {
      return { 'Profile': "/profile", 'Logout': null };
    }
    return { 'Sign In': "/login", 'Sign Up': "/register" };
  }, [isSignedIn]);

  const pages = useMemo(() => {
    if (role === 'admin') {
      return { 'Real Time': "/realtime", 'Admin Panel': "/admin" };
    }
    return { 'Real Time': "/realtime" };
  }, [isSignedIn, role]);

  function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  }

  function stringAvatar(name) {
    if (name) {
      return {
        sx: { bgcolor: stringToColor(name) },
        children: name[0] ?? 'U',
      };
    }
    return { src: "/static/images/avatar/2.jpg" };
  }

  const displayName = user?.username || user?.firstName || '';

  return (
    <AppBar position="static" sx={{ backgroundColor: "primary" }} data-testid="title">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Link sx={{ display: { xs: 'none', md: 'flex' }, m: 1, mr: 3 }} href="/">
            <Paper elevation={0} sx={{ height: "7vh", backgroundColor: "transparent" }}>
              <img style={{ height: "7vh" }} src="transit-tracker-icon-526-no-text.png" alt="transit-tracker-logo" />
            </Paper>
          </Link>

          <Typography variant="h6" noWrap component="a" href="/"
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' }, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '.3rem', color: 'inherit', textDecoration: 'none' }}>
            Transit Tracker
          </Typography>
          <ThemeToggleButton />

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton size="large" aria-label="account of current user" aria-controls="menu-appbar"
              aria-haspopup="true" onClick={handleOpenNavMenu} color="inherit">
              <MenuIcon />
            </IconButton>
            <Menu id="menu-appbar" anchorEl={anchorElNav}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              keepMounted transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(anchorElNav)} onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', md: 'none' } }}>
              {Object.keys(pages).map((page) => (
                <MenuItem key={page} onClick={handleCloseNavMenu}>
                  <Link href={pages[page]}>
                    <Typography sx={{ textAlign: 'center' }}>{page}</Typography>
                  </Link>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <AdbIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
          <Typography variant="h5" noWrap component="a" href="/"
            sx={{ mr: 2, display: { xs: 'flex', md: 'none' }, flexGrow: 1, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '.3rem', color: 'inherit', textDecoration: 'none' }}>
            Transit Tracker
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {Object.keys(pages).map((page) => (
              <Link key={page} href={pages[page]}>
                <Button onClick={handleCloseNavMenu} sx={{ my: 2, color: 'white', display: 'block' }}>
                  {page}
                </Button>
              </Link>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar alt={displayName} {...stringAvatar(displayName || null)} />
              </IconButton>
            </Tooltip>
            <Menu sx={{ mt: '45px' }} id="menu-appbar-user" anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)} onClose={handleCloseUserMenu}>
              {Object.keys(userMenuItems).map((item) => (
                <MenuItem key={item}
                  onClick={() => {
                    handleCloseUserMenu();
                    if (item === 'Logout') signOut();
                  }}>
                  {userMenuItems[item] ? (
                    <Link href={userMenuItems[item]}>
                      <Typography sx={{ textAlign: 'center' }}>{item}</Typography>
                    </Link>
                  ) : (
                    <Typography sx={{ textAlign: 'center' }}>{item}</Typography>
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default NavBar;
